// Shared time-warning visual language (Gary, 2026-09-28): red/yellow row + badge, unified
// across Pre-Devanning Plans, Schedule, Planned Outbound, Deliveries and Inbound. Each page
// keeps its own date-diff math (timestamps vs. date-only strings differ enough between pages
// that a single shared day-count function isn't safe) and only shares the *classification*
// (thresholds -> level) and the resulting styling, so "red" means the same severity everywhere.

export type OverdueLevel = 'ok' | 'warn' | 'danger' | 'severe'

export interface OverdueThresholds {
  /** days >= this -> at least 'warn' (e.g. a lead-up window before a due date, can be negative) */
  warnAtDays?: number
  /** days >= this -> at least 'danger' */
  dangerAtDays?: number
  /** days >= this -> 'severe' (optional 4th tier, e.g. Inbound's 14-day escalation) */
  severeAtDays?: number
}

export function classifyOverdue(
  days: number | null,
  { warnAtDays = Infinity, dangerAtDays = Infinity, severeAtDays = Infinity }: OverdueThresholds = {},
  active: boolean = true,
): OverdueLevel {
  if (!active || days == null) return 'ok'
  if (days >= severeAtDays) return 'severe'
  if (days >= dangerAtDays) return 'danger'
  if (days >= warnAtDays) return 'warn'
  return 'ok'
}

// Whole calendar days between two YYYY-MM-DD (or ISO-prefixed) date strings, `to` minus `from`.
export function daysBetween(fromISO: string, toISO: string): number {
  const from = new Date(fromISO.slice(0, 10) + 'T00:00:00')
  const to = new Date(toISO.slice(0, 10) + 'T00:00:00')
  return Math.round((to.getTime() - from.getTime()) / 86400000)
}

export const OVERDUE_ROW_CLASS: Record<OverdueLevel, string> = {
  ok:     '',
  warn:   'bg-yellow-50 dark:bg-yellow-900/10 hover:bg-yellow-100 dark:hover:bg-yellow-900/20',
  danger: 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20',
  severe: 'bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/40',
}

export const OVERDUE_TEXT_CLASS: Record<OverdueLevel, string> = {
  ok:     'text-gray-500',
  warn:   'font-semibold text-yellow-700 dark:text-yellow-400',
  danger: 'font-semibold text-red-700 dark:text-red-400',
  severe: 'font-bold text-red-700 dark:text-red-300',
}

// "Nd" for a lead-up warning (days until due), "Nd over" once at/past danger severity.
export function overdueBadgeText(days: number, level: OverdueLevel): string {
  if (level === 'ok') return ''
  const n = Math.abs(days)
  return level === 'warn' ? `⚠ ${n}d` : `⚠ ${n}d over`
}
